/**
 * Station services view models (M3 plan R16, §2.9 flow 2): what each tab shows and which command each button
 * sends, built from the Reach state and the career port. Panels only read state and dispatch commands; the
 * commands' owners (R12 runtime, R18 exploration, R19 relink) decide and refuse. Pure; UI-time only.
 */
import type { StationService, StationSite } from '../../../galaxy/layout/types'
import type { CareerPort } from '../../ports'
import type { ReachCore } from '../../runtime/types'
import type { StandInPrices } from '../../standin'
import { unsoldCharts, unsoldTotal } from '../../state/charts'
import { PART_IDS, type ReachCommand, type ReachCommandError, type ReachCommandResult } from '../../types'
import { formatCredits, formatMk, formatRange, formatTonnes, groupDigits, partLabel } from './format'

export type LineTone = 'normal' | 'dim' | 'good'

export interface ViewLine {
  label: string
  value: string
  /** A short marker after the value ("First discovery ×2", "Your home"). */
  note?: string
  tone?: LineTone
}

export interface ViewAction {
  id: string
  label: string
  enabled: boolean
  primary?: boolean
  /** Sent through `core.command` when chosen. */
  command: ReachCommand
}

export interface ViewGroup {
  heading: string | null
  lines: readonly ViewLine[]
  actions: readonly ViewAction[]
}

export interface PanelView {
  summary: readonly ViewLine[]
  groups: readonly ViewGroup[]
  /** Shown instead of the groups when there is nothing to list. */
  empty: string | null
}

/** The line under a panel after a command: its outcome. */
export interface InlineMessage {
  text: string
  ok: boolean
}

// ---------------------------------------------------------------------------------------------
// Tabs and their services
// ---------------------------------------------------------------------------------------------

export type ServiceTabId = 'charts' | 'parts' | 'scanner' | 'home'

export interface ServiceTabDef {
  id: ServiceTabId
  title: string
  order: number
  /** The station service the tab needs; null = every station. */
  service: StationService | null
}

/** Orders leave room for M2's tabs (market, repair, contracts) between them at the bridge. */
export const SERVICE_TABS: readonly ServiceTabDef[] = [
  { id: 'charts', title: 'Charts', order: 30, service: 'chart-buyer' },
  { id: 'parts', title: 'Parts', order: 40, service: 'parts-vendor' },
  { id: 'scanner', title: 'Scanner', order: 50, service: 'outfitting' },
  { id: 'home', title: 'Home', order: 90, service: null },
]

export function tabAvailable(tab: ServiceTabDef, station: StationSite): boolean {
  return tab.service === null || station.services.includes(tab.service)
}

// ---------------------------------------------------------------------------------------------
// Command outcomes
// ---------------------------------------------------------------------------------------------

const REFUSALS: Readonly<Record<Exclude<ReachCommandError, 'invalid'>, string>> = {
  funds: 'Not enough credits',
  cargo: 'Hold full',
  outOfRange: 'Out of hail range',
  unavailable: 'Not available here',
  busy: 'Busy, try again in a moment',
}

/** The inline text for a refusal. `invalid` carries the owner's own reason ("Already fitted with Mk III"). */
export function refusalMessage(error: ReachCommandError, message: string): string {
  if (error === 'invalid') return message.length > 0 ? message : 'Not possible'
  return REFUSALS[error]
}

export function resultMessage(result: ReachCommandResult): InlineMessage {
  if (result.ok) return { text: result.message.length > 0 ? result.message : 'Done', ok: true }
  return { text: refusalMessage(result.error, result.message), ok: false }
}

// ---------------------------------------------------------------------------------------------
// Charts (chart-buyer)
// ---------------------------------------------------------------------------------------------

export function chartsView(core: ReachCore, station: StationSite, firstDiscoveryMult: number | null): PanelView {
  const rows = unsoldCharts(core.state)
  const total = unsoldTotal(core.state)
  const first = core.state.charts.firstDiscoveries
  const note = firstDiscoveryMult === null ? 'First discovery' : `First discovery ×${firstDiscoveryMult}`
  const lines: ViewLine[] = rows.map(([systemId, value]) => {
    const name = core.galaxy.systems.find((s) => s.id === systemId)?.name ?? systemId
    const line: ViewLine = { label: name, value: formatCredits(value) }
    if (first.includes(systemId)) {
      line.note = note
      line.tone = 'good'
    }
    return line
  })
  return {
    summary: [
      { label: 'Unsold chart data', value: formatCredits(total) },
      { label: 'Systems', value: groupDigits(rows.length), tone: 'dim' },
    ],
    groups:
      rows.length === 0
        ? []
        : [
            { heading: 'By system', lines, actions: [] },
            {
              heading: null,
              lines: [{ label: 'Total', value: formatCredits(total) }],
              actions: [{ id: 'sell-all', label: 'Sell all', enabled: total > 0, primary: true, command: { kind: 'sellCharts', stationId: station.id } }],
            },
          ],
    empty: rows.length === 0 ? 'No unsold chart data. Chart new systems and scan for points of interest to earn it.' : null,
  }
}

// ---------------------------------------------------------------------------------------------
// Parts (parts-vendor)
// ---------------------------------------------------------------------------------------------

/** Float tolerance so, for example, 20 t of free space over 1 t parts never floors to 19. */
const FIT_EPSILON = 1e-9

/**
 * The "Fill" quantity: as many as fit the hold and the wallet, at least 1, so a full hold or an empty wallet
 * reaches the command and comes back as its refusal ("Hold full", "Not enough credits").
 */
export function fillQuantity(freeT: number, massT: number, credits: number, price: number): number {
  const fits = massT > 0 ? Math.floor(freeT / massT + FIT_EPSILON) : 0
  const affordable = price > 0 ? Math.floor(credits / price + FIT_EPSILON) : fits
  return Math.max(1, Math.min(fits, affordable))
}

/**
 * The Parts tab. Under the strategic economy (SY-2e) each part shows the station's curve price for one unit and its
 * stock ("Not sold here" when it doesn't sell the part), and Fill stops at the stock; the offline stand-in shows
 * `partPrices`.
 */
export function partsView(career: CareerPort, prices: StandInPrices, station: StationSite): PanelView {
  const credits = career.credits()
  const freeT = career.cargoFreeT()
  const groups: ViewGroup[] = PART_IDS.map((part) => {
    const quote = career.goodQuote?.(station.id, part) ?? null
    const price = quote === null ? prices.parts[part] : (quote.unit ?? 0)
    const massT = prices.massT[part]
    const fill = fillQuantity(freeT, massT, credits, price)
    const buy = (id: string, label: string, qty: number): ViewAction => ({
      id: `${part}-${id}`,
      label,
      enabled: true,
      command: { kind: 'buyParts', stationId: station.id, part, qty },
    })
    const lines: ViewLine[] =
      quote === null
        ? [{ label: 'Price', value: `${formatCredits(price)} / unit` }]
        : quote.unit === null
          ? [{ label: 'Price', value: 'Not sold here', tone: 'dim' }]
          : [
              { label: 'Price', value: `${formatCredits(quote.unit)} / unit` },
              { label: 'Stock', value: groupDigits(quote.stock), tone: quote.stock > 0 ? 'normal' : 'dim' },
            ]
    lines.push({ label: 'Carried', value: groupDigits(career.cargo(part)) }, { label: 'Mass', value: `${formatTonnes(massT)} / unit`, tone: 'dim' })
    return {
      heading: partLabel(part),
      lines,
      actions: [buy('1', '+1', 1), buy('5', '+5', 5), buy('fill', 'Fill', quote === null ? fill : Math.max(1, Math.min(fill, quote.stock)))],
    }
  })
  return {
    summary: [
      { label: 'Credits', value: formatCredits(credits) },
      { label: 'Free hold', value: formatTonnes(freeT) },
    ],
    groups,
    empty: null,
  }
}

// ---------------------------------------------------------------------------------------------
// Scanner (outfitting)
// ---------------------------------------------------------------------------------------------

const UPGRADES: readonly (2 | 3)[] = [2, 3]

export function scannerView(career: CareerPort, prices: StandInPrices, rangesM: readonly (number | null)[] | null, station: StationSite): PanelView {
  const current = career.scannerMk()
  const range = (mk: 1 | 2 | 3): string => formatRange(rangesM === null ? undefined : rangesM[mk - 1])
  return {
    summary: [
      { label: 'Fitted', value: formatMk(current) },
      { label: 'Deep-scan range', value: range(current) },
      { label: 'Credits', value: formatCredits(career.credits()), tone: 'dim' },
    ],
    groups: UPGRADES.map((mk) => {
      const fitted = mk === current
      const superseded = mk < current
      const label = fitted ? 'Fitted' : superseded ? 'Superseded' : `Buy ${formatMk(mk)}`
      return {
        heading: `Scanner ${formatMk(mk)}`,
        lines: [
          { label: 'Price', value: formatCredits(prices.scanners[mk]), tone: fitted || superseded ? 'dim' : 'normal' },
          { label: 'Deep-scan range', value: range(mk) },
        ],
        actions: [{ id: `mk${mk}`, label, enabled: mk > current, primary: mk === current + 1, command: { kind: 'buyScanner', stationId: station.id, mk } }],
      }
    }),
    empty: null,
  }
}

// ---------------------------------------------------------------------------------------------
// Home
// ---------------------------------------------------------------------------------------------

/** True when this station is the saved home. */
export function isHomeStation(core: ReachCore, station: StationSite): boolean {
  const layout = core.session.layout
  const home = core.state.home
  return home.stationId === station.id && (layout === null || layout.systemId === home.systemId)
}

/** The home station's name, or its system's when it is elsewhere ("Charter Hub", "Harrow's Reach"). */
function homeName(core: ReachCore): string {
  const home = core.state.home
  const layout = core.session.layout
  if (layout !== null && layout.systemId === home.systemId) {
    const st = layout.stations.find((s) => s.id === home.stationId)
    if (st) return st.name
  }
  const sys = core.galaxy.systems.find((s) => s.id === home.systemId)
  return sys ? `A station in ${sys.name}` : 'None'
}

export function homeView(core: ReachCore, station: StationSite): PanelView {
  const here = isHomeStation(core, station)
  return {
    summary: [
      { label: 'Home station', value: homeName(core), note: here ? 'This station' : undefined, tone: here ? 'good' : 'normal' },
      { label: 'Respawn', value: 'At your home station', tone: 'dim' },
    ],
    groups: [
      {
        heading: null,
        lines: [],
        actions: [
          { id: 'set-home', label: here ? 'Home is here' : 'Set as home', enabled: !here, primary: !here, command: { kind: 'setHome', stationId: station.id } },
          { id: 'save-now', label: 'Save now', enabled: true, command: { kind: 'saveNow' } },
        ],
      },
    ],
    empty: null,
  }
}
